import { ExperimentOutlined } from "@ant-design/icons";
import alignExample from "@images/align-example.png";
import alignStitchingExample from "@images/align-stitching-example.jpg";
import { Avatar, Card, List, Space, Tag, Typography } from "antd";
import type React from "react";
import { useCallback } from "react";
import { ColorWKBlue } from "theme";
import { APIJobCommand } from "types/api_types";
import { useAlignmentJobContext } from "./ai_alignment_job_context";

const { Text } = Typography;

export type AlignmentTask = {
  name: string;
  comment: string;
  id: string;
  jobType: APIJobCommand | null;
  image: string;
  disabled?: boolean;
};

const alignmentTasks: AlignmentTask[] = [
  {
    name: "Align Sections",
    comment:
      "Aligns all sections of this dataset along the Z axis using features in neighboring sections. Only supported for datasets with a single tile per sections (no stitching needed).",
    id: "align-sections",
    jobType: APIJobCommand.ALIGN_SECTIONS,
    image: alignExample,
  },
  {
    name: "Align & stitch multiple tiles",
    comment:
      "For stitching and aligning datasets with multiple tiles per section, please contact us via email for a quote.",
    id: "align-tiles",
    disabled: true,
    jobType: null,
    image: alignStitchingExample,
  },
];

export const AiAlignmentModelSelector: React.FC = () => {
  const { selectedTask, setSelectedTask } = useAlignmentJobContext();

  const handleTaskSelection = useCallback(
    (item: AlignmentTask) => {
      if (!item.disabled && item.jobType) {
        setSelectedTask(item);
      }
    },
    [setSelectedTask],
  );

  return (
    <Card
      type="inner"
      title={
        <Space align="center">
          <ExperimentOutlined style={{ color: ColorWKBlue }} />
          Select AI Alignment Task
        </Space>
      }
    >
      <List
        itemLayout="horizontal"
        dataSource={alignmentTasks}
        renderItem={(item) => (
          <List.Item
            className={"hoverable-list-item " + (selectedTask?.id === item.id ? "selected" : "")}
            style={{
              opacity: item.disabled ? 0.5 : 1,
              cursor: item.disabled ? "not-allowed" : "pointer",
            }}
            onClick={() => handleTaskSelection(item)}
          >
            <List.Item.Meta
              avatar={<Avatar shape="square" size={64} src={item.image} alt={item.name} />}
              title={
                <Space>
                  <Text strong>{item.name}</Text>
                  {item.disabled && <Tag>Coming Soon</Tag>}
                </Space>
              }
              description={item.comment}
            />
          </List.Item>
        )}
      />
    </Card>
  );
};
